import React, { useEffect, useState } from 'react';
import { de } from 'date-fns/locale';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { parseGermanDate } from '@/components/ui/date-input';
import api from '@/lib/axios';

const STORAGE_KEY = 'global_footer_settings';
const thisYear = new Date().getFullYear();
const todayIso = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const DEFAULTS = {
  zeitraumOn: false, von: `${thisYear}-01-01`, bis: `${thisYear}-12-31`,
  nurAktive: false, stichtagOn: false, stichtag: todayIso(),
  gkzGroupId: '', gkz: '',
};

export const getFooterSettings = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return { ...DEFAULTS, ...(stored || {}) };
  } catch {
    return { ...DEFAULTS };
  }
};

export const footerToParams = (f) => {
  const p = {};
  if (f.nurAktive) p.active_only = true;
  if (f.zeitraumOn) {
    if (f.von) p.period_start = f.von;
    if (f.bis) p.period_end = f.bis;
  }
  if (f.stichtagOn && f.stichtag) p.active_on = f.stichtag;
  if (f.gkzGroupId) p.gkz_group_id = f.gkzGroupId;
  if (f.gkz) p.gkz = f.gkz;
  return p;
};

/** Persist footer settings + notify all listeners (Startfenster, UV, …). */
export const setFooterSettings = (nextOrPatch) => {
  const cur = getFooterSettings();
  const next = typeof nextOrPatch === 'function'
    ? nextOrPatch(cur)
    : { ...cur, ...nextOrPatch };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  window.dispatchEvent(new CustomEvent('footer-settings-changed', { detail: next }));
  api.put('/settings/ui/footer', next).catch(() => {});
  return next;
};

/** Wie `gkz_key` im Backend — "Schü" und "schue" sind derselbe Code. */
const gkzKey = (value) => (value || '').trim().toLowerCase()
  .replace(/ü/g, 'ue').replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ß/g, 'ss')
  .replace(/ /g, '');

const fmt = (iso) => (iso ? iso.split('-').reverse().join('.') : '');
const isoOf = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const dateOf = (iso) => (iso ? new Date(`${iso}T12:00:00`) : undefined);

const DateButton = ({ value, onChange, testid, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(fmt(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setText(fmt(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commitText = () => {
    const parsed = parseGermanDate(text);
    if (parsed === null || parsed === undefined) { setText(fmt(value)); return; }
    onChange(isoOf(parsed));
    setText(fmt(isoOf(parsed)));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div title={'Datum eintippen (z. B. 15.8.26), ↑/↓ = Tag vor/zurück, Shift+↑/↓ = Woche, Kalender über das Icon'}
             className="flex items-center gap-1.5 bg-brand-canvas border border-brand-border rounded px-2 py-0.5 text-[11px] text-brand-text focus-within:border-brand-red min-w-[92px]">
          <PopoverTrigger asChild>
            <button type="button" tabIndex={-1} className="shrink-0 text-brand-muted hover:text-brand-anthracite" aria-label="Kalender öffnen">
              <CalendarDays className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <input
            type="text"
            inputMode="numeric"
            value={text}
            placeholder={placeholder || 'offen'}
            onChange={(e) => setText(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => { setFocused(false); commitText(); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitText(); e.target.blur(); }
              if (e.key === 'Escape') { setText(fmt(value)); e.target.blur(); }
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault();
                const base = parseGermanDate(text) || dateOf(value) || new Date();
                const step = e.key === 'ArrowUp' ? 1 : -1;
                base.setDate(base.getDate() + (e.shiftKey ? step * 7 : step));
                const iso = isoOf(base);
                onChange(iso);
                setText(fmt(iso));
              }
            }}
            className="w-[72px] bg-transparent outline-none border-none p-0 text-[11px] text-brand-text placeholder:text-brand-muted"
            data-testid={testid}
          />
        </div>
      </PopoverAnchor>
      <PopoverContent side="top" align="start" className="w-auto p-0 z-[80] bg-white" onOpenAutoFocus={(e) => e.preventDefault()}>
        <Calendar
          mode="single"
          locale={de}
          selected={dateOf(value)}
          defaultMonth={dateOf(value) || new Date()}
          onSelect={(d) => { if (d) { onChange(isoOf(d)); setText(fmt(isoOf(d))); setOpen(false); } }}
        />
      </PopoverContent>
    </Popover>
  );
};

export default function FooterDateBar() {
  const [footer, setFooterState] = useState(getFooterSettings);
  const [showMenu, setShowMenu] = useState(false);
  const [countText, setCountText] = useState('');
  const [gkzGroups, setGkzGroups] = useState([]);
  const [gkzCodes, setGkzCodes] = useState(null);
  const [gkzLoaded, setGkzLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get('/gkz-groups').then((r) => r.data || []).catch(() => []),
      api.get('/gkz-groups/codes').then((r) => (Array.isArray(r.data) ? r.data : null)).catch(() => null),
    ]).then(([groups, codes]) => {
      if (!alive) return;
      setGkzGroups(groups);
      setGkzCodes(codes);
      setGkzLoaded(true);
    });
    return () => { alive = false; };
  }, []);

  const setFooter = (next) => setFooterSettings(next);

  // GKZ-Filter gehören zu genau einem Mandanten. Ein Wert, den der Mandant nicht
  // kennt, filtert serverseitig jede Liste auf 0 — das darf nicht ungesehen
  // hängenbleiben, etwa nach einem Mandantenwechsel.
  useEffect(() => {
    if (!gkzLoaded) return;
    const patch = {};
    if (footer.gkzGroupId && !gkzGroups.some((group) => group.id === footer.gkzGroupId)) {
      patch.gkzGroupId = '';
    }
    if (footer.gkz && gkzCodes && !gkzCodes.some((code) => gkzKey(code) === gkzKey(footer.gkz))) {
      patch.gkz = '';
    }
    if (!Object.keys(patch).length) return;
    setFooter({ ...footer, ...patch });
  }, [footer, gkzGroups, gkzCodes, gkzLoaded]);

  // Externe Updates (z. B. Startfenster-Button) in die Fußleiste spiegeln
  useEffect(() => {
    const h = (e) => {
      if (e.detail) setFooterState(e.detail);
    };
    window.addEventListener('footer-settings-changed', h);
    return () => window.removeEventListener('footer-settings-changed', h);
  }, []);

  // Beim Laden: serverseitig gespeicherte Einstellungen des Benutzers übernehmen
  useEffect(() => {
    api.get('/settings/ui/footer').then((r) => {
      if (!r.data || Object.keys(r.data).length === 0) return;
      const merged = { ...DEFAULTS, ...r.data };
      const cur = getFooterSettings();
      if (JSON.stringify(cur) === JSON.stringify(merged)) return;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
      setFooterState(merged);
      window.dispatchEvent(new CustomEvent('footer-settings-changed', { detail: merged }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const h = (e) => setCountText(e.detail || '');
    window.addEventListener('footer-count', h);
    return () => window.removeEventListener('footer-count', h);
  }, []);

  const zeitraumPreset = (preset) => {
    const now = new Date();
    const y = now.getFullYear();
    const t = todayIso();
    let von; let bis;
    if (preset === 'heute') { von = t; bis = t; }
    else if (preset === 'ab_heute') { von = t; bis = ''; }
    else if (preset === 'monat') { von = isoOf(new Date(y, now.getMonth(), 1)); bis = isoOf(new Date(y, now.getMonth() + 1, 0)); }
    else if (preset === 'halbjahr') {
      const h1 = now.getMonth() < 6;
      von = `${y}-${h1 ? '01' : '07'}-01`;
      bis = h1 ? `${y}-06-30` : `${y}-12-31`;
    } else { von = `${y}-01-01`; bis = `${y}-12-31`; }
    setFooter({ ...footer, zeitraumOn: true, von, bis });
    setShowMenu(false);
  };

  const shiftMonth = (delta) => {
    const base = (footer.zeitraumOn && footer.von)
      ? new Date(`${footer.von}T12:00:00`)
      : new Date();
    const d = new Date(base.getFullYear(), base.getMonth() + delta, 1);
    const von = isoOf(d);
    const bis = isoOf(new Date(d.getFullYear(), d.getMonth() + 1, 0));
    setFooter({
      ...footer,
      zeitraumOn: true,
      von,
      bis,
      stichtagOn: true,
      stichtag: von,
    });
  };

  return (
    <div
      className="flex-shrink-0 z-[60] bg-white text-brand-text border-t border-brand-border px-4 py-2 flex items-center gap-6 text-xs"
      data-testid="uv-footer-bar"
      onMouseDown={(e) => {
        // Suchfeld-Fokus behalten (Vorschläge bleiben), Checkbox/Button trotzdem bedienbar
        const active = document.activeElement;
        const fromSearch = active && active.matches?.('input:not([type="checkbox"]):not([type="radio"]), textarea');
        if (!fromSearch) return;
        if (e.target.closest('input[type="checkbox"], button, label')) {
          e.preventDefault();
        }
      }}
    >
      {/* Zeitraum */}
      <div className="relative flex items-center gap-2">
        <label className="flex items-center gap-1.5 cursor-pointer select-none text-brand-muted">
          <input type="checkbox" checked={footer.zeitraumOn}
                 onChange={(e) => setFooter({ ...footer, zeitraumOn: e.target.checked })}
                 className="accent-brand-red" data-testid="footer-zeitraum-toggle" />
          Zeitraum
        </label>
        <DateButton value={footer.von} testid="footer-zeitraum-von"
                    onChange={(v) => setFooter({ ...footer, von: v, zeitraumOn: true })} />
        <span className="text-brand-muted">–</span>
        <DateButton value={footer.bis} testid="footer-zeitraum-bis"
                    onChange={(v) => setFooter({ ...footer, bis: v, zeitraumOn: true })} />
        <button type="button" onClick={() => setShowMenu(!showMenu)}
                className="px-1.5 py-0.5 rounded hover:bg-brand-canvas text-brand-muted"
                title="Zeitraum-Vorlagen" data-testid="footer-zeitraum-menu-btn">⚙</button>
        {showMenu && (
          <div className="absolute bottom-full mb-2 left-0 bg-white text-brand-text border border-brand-border rounded-lg shadow-xl py-1 w-48 z-[80]"
               data-testid="footer-zeitraum-menu">
            {[['heute', 'heute'], ['ab_heute', 'ab heute'], ['monat', 'aktueller Monat'], ['halbjahr', 'aktuelles Halbjahr'], ['jahr', 'aktuelles Jahr']].map(([v, l]) => (
              <button type="button" key={v} onClick={() => zeitraumPreset(v)}
                      data-testid={`footer-preset-${v}`}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-brand-canvas">
                {l}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Nur Aktive */}
      <label className="flex items-center gap-1.5 cursor-pointer select-none text-brand-muted">
        <input type="checkbox" checked={footer.nurAktive}
               onChange={(e) => setFooter({ ...footer, nurAktive: e.target.checked })}
               className="accent-brand-red" data-testid="footer-nur-aktive-toggle" />
        Nur Aktive
      </label>

      {/* Aktiv ab: Belegungen, die ab diesem Datum noch gelten */}
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1.5 cursor-pointer select-none text-brand-muted">
          <input type="checkbox" checked={footer.stichtagOn}
                 onChange={(e) => setFooter({ ...footer, stichtagOn: e.target.checked })}
                 className="accent-brand-red" data-testid="footer-stichtag-toggle" />
          Aktiv ab
        </label>
        <DateButton value={footer.stichtag} testid="footer-stichtag-datum"
                    onChange={(v) => setFooter({ ...footer, stichtag: v, stichtagOn: true })} />
        <button type="button" onClick={() => setFooter({ ...footer, stichtag: todayIso(), stichtagOn: true })}
                className="px-2 py-0.5 rounded border border-brand-border hover:bg-brand-canvas text-[11px] text-brand-text"
                data-testid="footer-stichtag-heute">Heute</button>
        <button type="button" onClick={() => shiftMonth(-1)}
                className="p-0.5 rounded hover:bg-brand-canvas text-brand-muted" title="Vorheriger Monat"
                data-testid="footer-month-prev">
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => shiftMonth(1)}
                className="p-0.5 rounded hover:bg-brand-canvas text-brand-muted" title="Nächster Monat"
                data-testid="footer-month-next">
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {gkzGroups.length > 0 && (
        <label className="flex items-center gap-1.5 text-brand-muted">
          <span className="whitespace-nowrap">GKZ-Gruppe</span>
          <select
            value={footer.gkzGroupId || ''}
            onChange={(e) => setFooter({ ...footer, gkzGroupId: e.target.value, gkz: '' })}
            className="bg-brand-canvas border border-brand-border rounded px-1.5 py-0.5 text-[11px] text-brand-text max-w-[11rem]"
            data-testid="footer-gkz-group"
          >
            <option value="">alle</option>
            {gkzGroups.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </label>
      )}

      {/* Einzelner GKZ-Code (z. B. per Sprachbefehl gesetzt) — sichtbar, sonst
          filtert er unbemerkt jede Liste. */}
      {footer.gkz && (
        <span className="flex items-center gap-1 rounded border border-brand-border bg-brand-canvas px-1.5 py-0.5 text-[11px] text-brand-text"
              data-testid="footer-gkz-code">
          GKZ {footer.gkz}
          <button type="button" onClick={() => setFooter({ ...footer, gkz: '' })}
                  className="text-brand-muted hover:text-brand-red" aria-label="GKZ-Filter entfernen"
                  data-testid="footer-gkz-code-clear">×</button>
        </span>
      )}

      <div className="ml-auto flex items-center gap-3">
        {(footer.zeitraumOn || footer.nurAktive || footer.stichtagOn || footer.gkzGroupId || footer.gkz) && (
          <button type="button" onClick={() => setFooter({ ...DEFAULTS, stichtag: todayIso() })}
                  className="px-2.5 py-1 rounded bg-brand-red text-white font-semibold text-[11px] hover:bg-brand-red/90"
                  data-testid="footer-reset">Filter zurücksetzen</button>
        )}
        {countText && <span className="text-brand-muted" data-testid="footer-count">{countText}</span>}
      </div>
    </div>
  );
}
